import { Dispatch, SetStateAction, useEffect, useMemo } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { useAtom, useSetAtom } from 'jotai'
import isEmpty from 'lodash-es/isEmpty'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMatch, useNavigate } from 'react-router-dom'
import { useFeatureFlags } from '@node-space/hooks'
import { DateErrorType, toast } from '@node-space/ui-components'
import { UKRAINE } from 'constants/contacts'
import { PathNames } from 'constants/General'
import { dateInitialErrors, useDateInput } from 'hooks/forms/useDateInput'
import { useFormFields } from 'hooks/forms/useFormFields'
import { usePostContactMutation } from 'hooks/mutations/usePostContact'
import { usePutContactMutation } from 'hooks/mutations/usePutContactMutation'
import { useFilteredCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { ContactInstrumentActionIds } from 'types/atoms/atom.contacts'
import { BaseErrorResponse, BeneficiaryTransferDestination } from 'types/beneficiaries'
import {
  ContactEntity,
  ContactInstrumentDecisionStep,
  ContactPayload,
  InstrumentType,
  PutContactProps,
  RelationshipType,
} from 'types/contacts'
import {
  contactActionAtom,
  contactAtom,
  contactInstrumentActionAtom,
  contactInstrumentDecisionStepAtom,
  editingContactAtom,
} from '../atoms/contactAtoms'
import { ContactFormData, ContactFormKeys, contactSchema } from '../schemas/contactSchema'
import { ukraineRegionOptions } from '../utils/constants'
import { getContactDefaultValues, handleContactOnError } from '../utils/utils'

interface UseContactFormProps {
  updateRequiredForBankAccountDestination?: BeneficiaryTransferDestination
  requiredFieldsForContactUpdate?: ContactFormKeys[]
  setDateErrors: Dispatch<SetStateAction<DateErrorType>>
}

export const useContactForm = ({
  updateRequiredForBankAccountDestination,
  requiredFieldsForContactUpdate,
  setDateErrors,
}: UseContactFormProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { enableContactBankAccounts } = useFeatureFlags()
  const match = useMatch(PathNames.CRYPTO_CONTACTS)

  const setContactInstrumentDecisionStep = useSetAtom(contactInstrumentDecisionStepAtom)
  const [contactsAction] = useAtom(contactActionAtom)
  const { contactBeneficiary, flowType } = contactsAction?.meta || {}
  const [contact, setContact] = useAtom(contactAtom)
  const setEditingContact = useSetAtom(editingContactAtom)
  const [contactInstrumentAction, setContactInstrumentAction] = useAtom(contactInstrumentActionAtom)

  const { formatDateInput, formatDateData } = useDateInput()

  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()

  const { mutate: postContact, isPending: isPostingContact } = usePostContactMutation()
  const { mutate: updateContact, isPending: isUpdatingContact } = usePutContactMutation()
  const { data: countries, isPending: isCountriesLoading } = useFilteredCountriesQuery(true)

  const isCryptoFlow = useMemo(() => {
    return (
      flowType === InstrumentType.CRYPTO ||
      contactInstrumentAction?.meta?.flowType === InstrumentType.CRYPTO ||
      contactInstrumentAction?.meta?.contactInstrument?.type === InstrumentType.CRYPTO
    )
  }, [flowType, contactInstrumentAction])

  const countriesOptions = countries?.map(country => ({
    leftText: country?.name,
    value: country?.code,
  }))

  const formSchema = contactSchema({
    t,
    requiredFields: requiredFieldsForContactUpdate,
    enableContactBankAccounts,
    isCryptoFlow,
  })

  const assignBeneficiaryToContact = !!contactBeneficiary

  const isEditingContact = !isEmpty(contact) || (!isEmpty(contact) && assignBeneficiaryToContact)

  useEffect(() => {
    setEditingContact(isEditingContact)
  }, [isEditingContact, setEditingContact])

  const form = useForm<ContactFormData>({
    defaultValues: getContactDefaultValues(isEditingContact, contact, formatDateData),
    resolver: yupResolver(formSchema),
  })

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    watch,
    trigger,
    formState: { errors, isSubmitting },
  } = form

  const formValues = useWatch({ control })

  const isCompany = formValues?.entityType === ContactEntity.COMPANY
  const isIndividual = formValues?.entityType === ContactEntity.INDIVIDUAL
  const isUkraineSelected =
    (isIndividual && formValues?.countryOfResidence === UKRAINE) ||
    (isCompany && formValues?.companyRegistrationCountry === UKRAINE)

  const { setRadioField, setDatePickerField, setInputField, setCheckboxField, setComboboxField } =
    useFormFields<ContactFormKeys>(formSchema, form, formValues)

  const formFields: { [key in ContactFormKeys] } = {
    entityType: setRadioField({
      key: 'entityType',
      options: [
        { value: ContactEntity.COMPANY, label: t('contacts.company') },
        { value: ContactEntity.INDIVIDUAL, label: t('contacts.individual') },
      ],
      onChangeAdditional: () => {
        setDateErrors(dateInitialErrors)
        setValue('dateOfBirth', '')
        clearErrors()
        setRequestError({
          show: false,
        })
      },
    }),
    companyName: setInputField({
      key: 'companyName',
      placeholder: t('formFields.placeholder.companyName'),
    }),
    contactDescription: setInputField({
      key: 'contactDescription',
      placeholder: isCompany
        ? t('formFields.placeholder.contactCompanyDescription')
        : t('formFields.placeholder.contactPersonDescription'),
    }),
    companyRegistrationNumber: setInputField({
      key: 'companyRegistrationNumber',
      placeholder: t('formFields.companyRegistrationNumber'),
    }),
    companyRegistrationCountry: setComboboxField({
      key: 'companyRegistrationCountry',
      items: countriesOptions,
      placeholder: t('select'),
      defaultValue: watch('companyRegistrationCountry'),
    }),
    addressLine1: setInputField({
      key: 'addressLine1',
      placeholder: t('formFields.addressLine1'),
    }),
    addressLine2: setInputField({
      key: 'addressLine2',
      placeholder: t('formFields.addressLine2'),
    }),
    city: setInputField({
      key: 'city',
      placeholder: t('formFields.city'),
    }),
    postCode: setInputField({
      key: 'postCode',
      placeholder: t('formFields.postCode'),
    }),
    region: setInputField({
      key: 'region',
      placeholder: t('formFields.region'),
    }),
    regionCode: setComboboxField({
      key: 'regionCode',
      items: ukraineRegionOptions,
      placeholder: t('select'),
      defaultValue: watch('regionCode'),
    }),
    firstName: setInputField({
      key: 'firstName',
      placeholder: t('formFields.placeholder.firstNameAndMiddleNames'),
    }),
    lastName: setInputField({
      key: 'lastName',
      placeholder: t('formFields.placeholder.lastName'),
    }),
    countryOfResidence: setComboboxField({
      key: 'countryOfResidence',
      items: countriesOptions,
      placeholder: t('select'),
      defaultValue: watch('countryOfResidence'),
    }),
    isSelfOwned: setCheckboxField({
      key: 'isSelfOwned',
      name: 'isSelfOwned',
      id: 'isSelfOwned',
      testId: 'is-self-owned',
    }),
    dateOfBirth: setDatePickerField({ key: 'dateOfBirth' }),
  }

  const getRelationshipType = () => {
    if (isIndividual) {
      return RelationshipType.THIRD_PARTY
    }

    return formValues?.isSelfOwned ? RelationshipType.SELF_OWNED : RelationshipType.THIRD_PARTY
  }

  const onSubmit: SubmitHandler<typeof formValues> = () => {
    resetRequestError()
    const {
      firstName,
      lastName,
      companyName,
      contactDescription,
      addressLine1,
      addressLine2,
      city,
      postCode,
      region,
      regionCode,
      countryOfResidence,
      companyRegistrationNumber,
      companyRegistrationCountry,
      dateOfBirth,
    } = formValues || {}
    const hasAddress =
      !!addressLine1 || !!addressLine2 || !!city || !!postCode || !!region || !!regionCode

    const data: ContactPayload = {
      description: contactDescription,
      relationshipType: getRelationshipType(),
      entity: {
        ...(isCompany && {
          legalName: companyName,
          ...(companyRegistrationNumber && {
            registrationNumber: formValues?.companyRegistrationNumber,
          }),
        }),
        ...(isIndividual && {
          firstName: firstName,
          lastName: lastName,
          ...(dateOfBirth && { dateOfBirth: formatDateInput(dateOfBirth) }),
        }),
        ...(hasAddress && {
          address: {
            ...(addressLine1 && { addressLine1: addressLine1 }),
            ...(addressLine2 && { addressLine2: addressLine2 }),
            ...(city && { city: city }),
            ...(postCode && { postCode: postCode }),
            ...(region && { region: region }),
            ...(regionCode && { regionCode: regionCode }),
            ...(isIndividual && countryOfResidence && { country: countryOfResidence }),
            ...(isCompany && companyRegistrationCountry && { country: companyRegistrationCountry }),
          },
        }),
        type: formValues?.entityType,
      },
    }
    if (isEditingContact) {
      const payload: PutContactProps = {
        contact: data,
        contactId: contact?.id,
      }
      updateContact(payload, {
        onSuccess: response => {
          toast({
            header: t('contacts.updateContactSuccess', {
              contactName: response?.name,
            }),
            variant: 'success',
          })
          setContact(contactsAction?.meta?.keepUpdatedContact ? response : null)

          if (contactBeneficiary) {
            setContactInstrumentAction({
              ...contactInstrumentAction,
              actionId: ContactInstrumentActionIds?.FIAT_INSTRUMENT,
              header: t('contacts.addBankAccount'),
              onClose: () => {
                contactsAction?.onClose()
                setContact(null)
                setContactInstrumentAction(null)
                queryClient.refetchQueries({ queryKey: [reactQueryName.CONTACTS_PAYMENT] })
              },
            })
          } else {
            contactsAction?.onClose()
          }
        },
        onError: (error: BaseErrorResponse) => {
          handleContactOnError(error, setRequestError, data, 'usePutContactMutation', t)
        },
        onSettled: () => {
          queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACTS] })
          queryClient.refetchQueries({ queryKey: [reactQueryName.CONTACTS_PAYMENT] })
        },
      })
    } else {
      postContact(data, {
        onSuccess: response => {
          toast({
            header: t(
              enableContactBankAccounts
                ? 'contacts.addContactSuccessV2'
                : 'contacts.addContactSuccess',
              {
                contactName: response?.name,
              }
            ),
            variant: 'success',
          })
          setContact(response)

          if (contactBeneficiary) {
            setContactInstrumentAction({
              ...contactInstrumentAction,
              actionId: ContactInstrumentActionIds?.FIAT_INSTRUMENT,
              header: t('contacts.addBankAccount'),
              onClose: () => {
                contactsAction?.onClose()
                setContact(null)
                setContactInstrumentAction(null)
                queryClient.refetchQueries({ queryKey: [reactQueryName.CONTACTS_PAYMENT] })
              },
            })
          } else if (
            contactInstrumentAction?.actionId ===
            ContactInstrumentActionIds?.CONTACT_INSTRUMENT_DECISION
          ) {
            setContactInstrumentDecisionStep(ContactInstrumentDecisionStep.INSTRUMENT)
          } else if (contactsAction?.meta?.enableCreateInstrument) {
            setContactInstrumentAction({
              ...contactInstrumentAction,
              header: t('contacts.addCryptoAccount'),
              actionId: ContactInstrumentActionIds.INSTRUMENT,
              onClose: () => {
                contactsAction?.onClose()
                setContact(null)
                setContactInstrumentAction(null)
                queryClient.refetchQueries({ queryKey: [reactQueryName.CONTACTS_PAYMENT] })
              },
            })
          } else if (match) {
            navigate(`${PathNames.CRYPTO_CONTACTS}/${response?.id}`)
          } else {
            setContact(null)
            contactInstrumentAction?.onClose()
            queryClient.refetchQueries({ queryKey: [reactQueryName.CONTACTS_PAYMENT] })
          }
          queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACTS] })
        },
        onError: (error: BaseErrorResponse) => {
          handleContactOnError(error, setRequestError, data, 'usePostContactMutation', t)
        },
      })
    }
  }

  const onChangeCountry = (
    key: 'countryOfResidence' | 'companyRegistrationCountry',
    value: string
  ) => {
    setValue(key, value, {
      shouldValidate: true,
      shouldDirty: true,
    })
    value !== UKRAINE && trigger('regionCode')
  }

  return {
    formFields,
    formValues,
    isCountriesLoading,
    errors,
    control,
    isSubmitting,
    isPostingContact,
    isCompany,
    isIndividual,
    requestError,
    isEditingContact,
    isUpdatingContact,
    isUkraineSelected,
    isCryptoFlow,
    handleSubmit,
    onSubmit,
    setValue,
    onChangeCountry,
    trigger,
  }
}
